import { NgModule } from '@angular/core';
import { RouterModule, Routes, ExtraOptions } from '@angular/router';
import { LayoutComponent } from './layout/layout.component';
import { AuthGuard } from './core/guards/auth.guard';
import { GlobalAuthGuard } from './core/guards/global-auth.guard';
import { HotelSelectionGuard } from './core/guards/hotel-selection.guard';
import { HotelSelectionUrlGuard } from './core/guards/hotel-selection-url.guard';
import { RedirectLoggedGuard } from './core/guards/redirect-logged.guard';
import { AccessAreaGuard } from './core/guards/access-area.guard';
import { UserProfileComponent } from './pages/user-profile/user-profile.component';
import { NotificationCenterComponent } from './components/notification-center/notification-center.component';
import { OnboardingRequestsComponent } from './pages/onboarding-requests/onboarding-requests.component';
import { OnboardingRequestDetailComponent } from './pages/onboarding-requests/onboarding-request-detail/onboarding-request-detail.component';
import { HotelCreationComponent } from './pages/hotel-creation/hotel-creation.component';
import { SetupIncompleteComponent } from './pages/setup-incomplete/setup-incomplete.component';
import { AccessDeniedComponent } from './pages/access-denied/access-denied.component';

const routes: Routes = [
  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
  {
    path: 'account',
    loadChildren: () =>
      import('./account/account.module').then((m) => m.AccountModule),
    canActivate: [RedirectLoggedGuard],
  },
  // Setup incomplete page — AuthGuard only (GlobalAuthGuard would redirect here in a loop)
  {
    path: 'setup-incomplete',
    component: LayoutComponent,
    canActivate: [AuthGuard],
    children: [
      { path: '', component: SetupIncompleteComponent }
    ]
  },
  // Access denied page — must be before :hotelId wildcard to avoid treating "access-denied" as a hotel ID
  {
    path: 'access-denied',
    component: LayoutComponent,
    canActivate: [AuthGuard],
    children: [
      { path: '', component: AccessDeniedComponent }
    ]
  },
  {
    path: 'dashboard',
    component: LayoutComponent,
    canActivate: [AuthGuard, GlobalAuthGuard, HotelSelectionGuard],
    loadChildren: () =>
      import('./pages/pages.module').then((m) => m.PagesModule),
  },
  // User profile route - no hotel selection required (must be before :hotelId wildcard)
  {
    path: 'user-profile',
    component: LayoutComponent,
    canActivate: [AuthGuard, GlobalAuthGuard],
    children: [
      { path: '', component: UserProfileComponent }
    ]
  },
  // Notifications route - no hotel selection required
  {
    path: 'notifications',
    component: LayoutComponent,
    canActivate: [AuthGuard, GlobalAuthGuard],
    children: [
      { path: '', component: NotificationCenterComponent }
    ]
  },
  // Onboarding Requests - internal only, no hotel ID required
  {
    path: 'onboarding-requests',
    component: LayoutComponent,
    canActivate: [AuthGuard, GlobalAuthGuard, AccessAreaGuard],
    children: [
      { path: '', component: OnboardingRequestsComponent },
      { path: ':id', component: OnboardingRequestDetailComponent },
    ],
  },
  // Hotel Creation - super admin only, no hotel ID required
  {
    path: 'hotel-creation',
    component: LayoutComponent,
    canActivate: [AuthGuard, GlobalAuthGuard, AccessAreaGuard],
    children: [
      { path: '', component: HotelCreationComponent },
    ],
  },
  {
    path: ':hotelId',
    component: LayoutComponent,
    canActivate: [AuthGuard, GlobalAuthGuard, HotelSelectionUrlGuard, HotelSelectionGuard],
    loadChildren: () =>
      import('./pages/pages.module').then((m) => m.PagesModule),
  },
  { path: '**', redirectTo: 'dashboard', pathMatch: 'full' },
];

const routerOptions: ExtraOptions = {
  scrollPositionRestoration: 'top',
  paramsInheritanceStrategy: 'always',
};

RouterModule.forRoot(routes, routerOptions);

@NgModule({
  imports: [RouterModule.forRoot(routes, routerOptions)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
